import GenerateReport from "./GenerateReport";
import { useState } from "react";

// ═══════════════════════════════════════════════════════
// MATRICE DE SCORING — QUESTIONNAIRE B — 8-11 ANS
// ═══════════════════════════════════════════════════════

// 6 DIMENSIONS

// 6 PROFILS
const PROFILES = {
  INT: {
    name: "L'Intensif",
    emoji: "⚡",
    tagline: "Il se dépense sans compter — l'effort physique est son moteur",
    description: "Cet enfant apprend et s'épanouit par l'intensité physique. Il a besoin de bouger, de transpirer, de se dépasser. L'effort est pour lui une source d'énergie, pas une contrainte.",
    color: "#E8732A",
    bg: "#FFF0E6",
    top: [
      { emoji: "⚽", name: "Football", why: "Intensité constante, efforts courts et répétés, variété des situations" },
      { emoji: "🏊", name: "Natation sportive", why: "Effort physique pur, progression mesurable, solo ou collectif" },
      { emoji: "🏃", name: "Athlétisme", why: "Se dépasser soi-même, résultats immédiats, effort intense" },
      { emoji: "🧗", name: "Escalade", why: "Effort total, défis physiques constants, progression visible" },
    ],
    explore: ["Rugby / Touch rugby", "Arts martiaux (judo, karaté)", "Basketball", "Handball"],
    avoid: "Les activités trop lentes ou répétitives où l'enfant ne peut pas se dépenser (échecs, musique classique stricte, gym rythmique sans énergie).",
  },
  PRE: {
    name: "Le Technicien",
    emoji: "🎯",
    tagline: "Il veut maîtriser chaque détail — la perfection technique est son terrain",
    description: "Cet enfant est motivé par la maîtrise. Il aime comprendre comment les choses fonctionnent, progresser de façon mesurable, et sentir qu'il contrôle chaque geste. La répétition purposeful ne l'ennuie pas — elle le satisfait.",
    color: "#3D2B8E",
    bg: "#EDE8FF",
    top: [
      { emoji: "⚔️", name: "Escrime", why: "Précision technique, duel individuel, progression par classements" },
      { emoji: "🏹", name: "Tir à l'arc", why: "Concentration, geste parfait, résultats immédiats et mesurables" },
      { emoji: "🎾", name: "Tennis", why: "Technique exigeante, duel individuel, progression visible" },
      { emoji: "🥋", name: "Arts martiaux (kata)", why: "Maîtrise du geste, grades progressifs, discipline personnelle" },
    ],
    explore: ["Natation technique", "Piano ou instrument classique", "Équitation", "Golf"],
    avoid: "Les activités trop libres sans objectif mesurable, ou les sports collectifs où la technique individuelle est diluée.",
  },
  EXP: {
    name: "L'Artiste-Expressif",
    emoji: "🎨",
    tagline: "Il crée et s'exprime — son corps ou ses mains sont ses outils",
    description: "Cet enfant a besoin d'un espace d'expression libre. Il apprend mieux quand il peut mettre sa patte, créer quelque chose d'unique, et utiliser son corps ou ses mains comme outil d'expression. La liberté créative est non négociable.",
    color: "#9B4FBB",
    bg: "#F5E8FF",
    top: [
      { emoji: "💃", name: "Danse hip-hop ou contemporaine", why: "Expression corporelle libre, créativité, pas de cadre rigide" },
      { emoji: "🎵", name: "Musique — batterie ou guitare électrique", why: "Expression rythmique, résultats rapides, liberté de style" },
      { emoji: "🎭", name: "Théâtre", why: "Expression totale, liberté de création, performance devant les autres" },
      { emoji: "🎪", name: "Arts du cirque", why: "Corps expressif et acrobatique, création de numéros, spectacle" },
    ],
    explore: ["Capoeira", "Arts plastiques / sculpture", "Danse classique (si profil PRE secondaire)", "Chant"],
    avoid: "Les activités avec cadre trop rigide où chaque geste est imposé — gym rythmique stricte, musique classique avec solfège obligatoire dès le départ.",
  },
  COM: {
    name: "Le Compétiteur",
    emoji: "⚔️",
    tagline: "Il a besoin d'un adversaire — la compétition le révèle",
    description: "Cet enfant se dépasse en présence d'un adversaire. La compétition ne le stresse pas — elle le galvanise. Il a besoin d'un enjeu réel et de savoir clairement s'il gagne ou perd. L'environnement bienveillant mais exigeant lui convient parfaitement.",
    color: "#C62828",
    bg: "#FFEBEE",
    top: [
      { emoji: "🥊", name: "Sports de combat (judo, karaté, boxe anglaise)", why: "Duel pur, technique précise, progresser pour gagner" },
      { emoji: "⚔️", name: "Escrime", why: "Duel élégant, technique, résultats mesurables par compétition" },
      { emoji: "🎾", name: "Tennis en compétition", why: "Duel direct, classement, progression claire" },
      { emoji: "♟️", name: "Échecs", why: "Compétition mentale pure, stratégie, tournois dès 8 ans" },
    ],
    explore: ["Natation compétition", "Athlétisme compétition", "Tir à l'arc en compétition", "Badminton"],
    avoid: "Les activités sans enjeu compétitif clair, ou les sports collectifs où la responsabilité individuelle est diluée.",
  },
  COL: {
    name: "Le Collectif",
    emoji: "🤝",
    tagline: "Il s'épanouit dans le groupe — les liens humains sont son carburant",
    description: "Cet enfant a besoin du groupe pour donner le meilleur de lui-même. Les victoires partagées et les défaites collectives l'affectent profondément — dans le bon sens. L'ambiance et les relations comptent autant que la performance.",
    color: "#1B6B3A",
    bg: "#E8F5E9",
    top: [
      { emoji: "⚽", name: "Football", why: "Le sport collectif le plus accessible, victoires et défaites partagées" },
      { emoji: "🏀", name: "Basketball", why: "Jeu rapide, interdépendance forte, esprit d'équipe immédiat" },
      { emoji: "🎭", name: "Théâtre collectif", why: "Troupe soudée, création commune, spectacle partagé" },
      { emoji: "🎵", name: "Chorale ou musique d'ensemble", why: "Harmonie collective, appartenance forte, partage émotionnel" },
    ],
    explore: ["Handball", "Volleyball", "Rugby à 7", "Comédie musicale"],
    avoid: "Les activités strictement individuelles où l'enfant est seul face à sa performance — tir à l'arc, natation individuelle, échecs.",
  },
  SCE: {
    name: "Le Performeur",
    emoji: "🎭",
    tagline: "Il s'élève quand on le regarde — le public est son moteur",
    description: "Cet enfant donne son meilleur devant un public. Être regardé ne le stresse pas — cela l'énergise. Il a besoin de moments de représentation réguliers pour maintenir sa motivation. Les spectacles de fin d'année sont pour lui des événements majeurs.",
    color: "#1565C0",
    bg: "#E3F2FD",
    top: [
      { emoji: "🎭", name: "Théâtre", why: "Performance pure, public, représentations régulières, être regardé" },
      { emoji: "💃", name: "Danse de scène", why: "Corps expressif, public, spectacles — le format idéal" },
      { emoji: "🎪", name: "Arts du cirque", why: "Numéros devant public, acrobaties impressionnantes, show total" },
      { emoji: "🎵", name: "Musique — récitals et concerts", why: "Se produire devant les autres, être applaudi, montrer sa maîtrise" },
    ],
    explore: ["Gym rythmique et acrobatique", "Trampoline artistique", "Danse hip-hop (battles)", "Magie / prestidigitation"],
    avoid: "Les activités sans public ni représentation — natation pure, athlétisme sans public, arts martiaux sans tournois.",
  },
};

// ACTIVITÉS PAR COMBINAISON PROFIL PRINCIPAL + SECONDAIRE (nuances)

// ═══════════════════════════════════════════════════════
// QUESTIONS ENFANT (8 questions, réponse unique)
// ═══════════════════════════════════════════════════════
const CHILD_QUESTIONS = [
  {
    id: "C1", text: "Le samedi matin, tu as 2 heures pour toi. Qu'est-ce que tu préfères faire ?",
    options: [
      { id: "A", emoji: "🏃", text: "Me dépenser — courir, sauter, bouger à fond", scores: { INT: 3 } },
      { id: "B", emoji: "🎯", text: "M'entraîner à faire un geste parfaitement", scores: { PRE: 3 } },
      { id: "C", emoji: "💃", text: "Bouger en musique, danser, faire des figures", scores: { EXP: 3 } },
      { id: "D", emoji: "🎨", text: "Fabriquer ou créer quelque chose avec mes mains", scores: { EXP: 2, PRE: 1 } },
    ],
  },
  {
    id: "C2", text: "Dans ton activité idéale, tu es...",
    options: [
      { id: "A", emoji: "👤", text: "Seul — je fais mes progrès à mon rythme", textF: "Seule — je fais mes progrès à mon rythme", scores: { PRE: 2 } },
      { id: "B", emoji: "👥", text: "Dans une équipe — on gagne et on perd ensemble", scores: { COL: 3 } },
      { id: "C", emoji: "⚔️", text: "Face à quelqu'un — j'aime essayer de gagner contre un adversaire", scores: { COM: 3 } },
      { id: "D", emoji: "🎭", text: "Devant des spectateurs — j'aime quand on me regarde", scores: { SCE: 3 } },
    ],
  },
  {
    id: "C3", text: "Dans une activité, qu'est-ce qui te convient le mieux ?",
    options: [
      { id: "A", emoji: "🎯", text: "Apprendre des gestes précis et techniques — chaque mouvement a un sens", scores: { PRE: 2 } },
      { id: "B", emoji: "📋", text: "Suivre une progression organisée avec des niveaux clairs à franchir", scores: { PRE: 1, INT: 1 } },
      { id: "C", emoji: "🌊", text: "Pouvoir bouger et m'exprimer librement, sans trop de règles à respecter", scores: { EXP: 2 } },
      { id: "D", emoji: "🎲", text: "Varier les exercices et être surpris — j'aime quand ça change tout le temps", textF: "Varier les exercices et être surprise — j'aime quand ça change tout le temps", scores: { INT: 1, EXP: 1 } },
    ],
  },
  {
    id: "C4", text: "Après 3 mois d'entraînement, ce qui te rendrait le plus fier c'est...",
    options: [
      { id: "A", emoji: "🏆", text: "Gagner une compétition contre d'autres enfants", scores: { COM: 3 } },
      { id: "B", emoji: "📈", text: "Réussir un mouvement que je ne savais pas faire avant", scores: { PRE: 2 } },
      { id: "C", emoji: "🎬", text: "Faire une belle prestation devant un public", scores: { SCE: 3 } },
      { id: "D", emoji: "🤝", text: "Que mon équipe soit bien soudée et s'entende bien", scores: { COL: 3 } },
    ],
  },
  {
    id: "C5", text: "À la piscine, tu vois un grand plongeoir que tu n'as jamais sauté. Tu fais quoi ?",
    options: [
      { id: "A", emoji: "🚀", text: "Je monte et je saute tout de suite — j'adore les sensations fortes", scores: { INT: 2, COM: 1 } },
      { id: "B", emoji: "👀", text: "Je regarde d'abord comment les autres font, puis je saute", scores: { PRE: 1 } },
      { id: "C", emoji: "💬", text: "Je demande à un copain de sauter en même temps que moi", scores: { COL: 2 } },
      { id: "D", emoji: "🙅", text: "Je préfère nager et m'améliorer dans l'eau — le plongeoir ne m'attire pas", scores: { PRE: 2 } },
    ],
  },
  {
    id: "C6", text: "Pour montrer que tu es content, tu fais quoi en premier ?",
    options: [
      { id: "A", emoji: "🏃", text: "Je bouge — je cours, je saute, je m'agite", scores: { INT: 2 } },
      { id: "B", emoji: "🗣️", text: "Je le dis ou je chante", scores: { SCE: 1, EXP: 1 } },
      { id: "C", emoji: "🖐️", text: "Je dessine ou je fabrique quelque chose", scores: { EXP: 2 } },
      { id: "D", emoji: "🎵", text: "J'écoute de la musique ou je joue d'un instrument", scores: { EXP: 3 } },
    ],
  },
  {
    id: "C7", text: "Quel entraînement tu préfères ?",
    options: [
      { id: "A", emoji: "⚡", text: "Des exercices courts qui changent souvent — j'aime quand ça bouge", scores: { INT: 2 } },
      { id: "B", emoji: "🔄", text: "Répéter le même mouvement encore et encore jusqu'à le réussir parfaitement", scores: { PRE: 3 } },
      { id: "C", emoji: "🎲", text: "Des séances variées où on ne sait jamais ce qui va se passer", scores: { INT: 1, EXP: 1 } },
      { id: "D", emoji: "📖", text: "Comprendre comment faire avant de me lancer", scores: { PRE: 1 } },
    ],
  },
  {
    id: "C8", text: "Tu rates quelque chose d'important pendant un match ou un spectacle. Qu'est-ce que tu fais ?",
    options: [
      { id: "A", emoji: "💪", text: "Je reprends tout de suite — ça arrive, on continue", scores: { INT: 1, COM: 1 } },
      { id: "B", emoji: "🔍", text: "Je cherche à comprendre ce qui s'est passé pour ne plus recommencer", scores: { PRE: 2 } },
      { id: "C", emoji: "😔", text: "J'ai du mal à oublier — ça reste dans ma tête un moment", scores: { COM: 1 } },
      { id: "D", emoji: "😄", text: "Je ris et je passe à autre chose — c'est pas grave", scores: { EXP: 1 } },
    ],
  },
];

// ═══════════════════════════════════════════════════════
// QUESTIONS PARENT (12 questions, 2 réponses sauf Q10/Q11)
// ═══════════════════════════════════════════════════════
const PARENT_QUESTIONS = [
  {
    id: "P1", text: "Quand votre enfant a du temps libre non structuré, que fait-il spontanément ?",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Il se dépense physiquement — court, saute, grimpe", textF: "Elle se dépense physiquement — court, saute, grimpe", scores: { INT: 2 } },
      { id: "B", text: "Il s'installe et fabrique ou dessine quelque chose", textF: "Elle s'installe et fabrique ou dessine quelque chose", scores: { EXP: 1, PRE: 1 } },
      { id: "C", text: "Il écoute de la musique, chante ou danse", textF: "Elle écoute de la musique, chante ou danse", scores: { EXP: 2 } },
      { id: "D", text: "Il lit, observe ou réfléchit calmement", textF: "Elle lit, observe ou réfléchit calmement", scores: { PRE: 1 } },
      { id: "E", text: "Il cherche quelqu'un avec qui jouer ou parler", textF: "Elle cherche quelqu'un avec qui jouer ou parler", scores: { COL: 2 } },
    ],
  },
  {
    id: "P2", text: "Quand votre enfant perd à un jeu ou rate quelque chose, il...",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Repart immédiatement et veut recommencer", scores: { INT: 1 } },
      { id: "B", text: "Se met en colère puis reprend rapidement", scores: { COM: 1 } },
      { id: "C", text: "Se décourage et abandonne facilement", scores: {} },
      { id: "D", text: "Analyse calmement ce qui s'est passé", scores: { PRE: 1 } },
      { id: "E", text: "A besoin d'être encouragé avant de reprendre", textF: "A besoin d'être encouragée avant de reprendre", scores: { COL: 1 } },
    ],
  },
  {
    id: "P3", text: "Dans une activité collective, votre enfant a naturellement tendance à...",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Prendre les décisions et organiser le groupe", scores: { COM: 1 } },
      { id: "B", text: "Suivre et s'intégrer harmonieusement", scores: { COL: 2 } },
      { id: "C", text: "S'isoler et faire sa propre chose", scores: { PRE: 1 } },
      { id: "D", text: "Observer avant de s'engager", scores: { PRE: 1 } },
      { id: "E", text: "Veiller à ce que tout le monde soit inclus et se sente bien", scores: { COL: 2 } },
    ],
  },
  {
    id: "P4", text: "Qu'est-ce qui fatigue le plus votre enfant ?",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "L'effort physique intense et prolongé", scores: { EXP: 1, SCE: 1 } },
      { id: "B", text: "Répéter le même geste ou la même chose pendant longtemps", scores: { EXP: 2, INT: 1 } },
      { id: "C", text: "Les activités en groupe où il doit s'adapter aux autres", textF: "Les activités en groupe où elle doit s'adapter aux autres", scores: { PRE: 2 } },
      { id: "D", text: "Les règles strictes et le manque de liberté", scores: { EXP: 2 } },
      { id: "E", text: "Le silence et la solitude", scores: { COL: 2 } },
    ],
  },
  {
    id: "P5", text: "Dans quelle situation votre enfant vous semble le plus vivant, le plus lui-même ?",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Quand il se dépasse physiquement — effort, vitesse, intensité", textF: "Quand elle se dépasse physiquement — effort, vitesse, intensité", scores: { INT: 2 } },
      { id: "B", text: "Quand il crée quelque chose de ses mains — construit, dessine, invente", textF: "Quand elle crée quelque chose de ses mains — construit, dessine, invente", scores: { EXP: 2 } },
      { id: "C", text: "Quand il performe devant un public — spectacle, représentation", textF: "Quand elle performe devant un public — spectacle, représentation", scores: { SCE: 3, EXP: 1 } },
      { id: "D", text: "Quand il résout un problème difficile — réfléchit, cherche, trouve", textF: "Quand elle résout un problème difficile — réfléchit, cherche, trouve", scores: { PRE: 1 } },
      { id: "E", text: "Quand il est entouré de ses amis et partage un bon moment", textF: "Quand elle est entourée de ses amis et partage un bon moment", scores: { COL: 2 } },
    ],
  },
  {
    id: "P6", text: "Face à une nouvelle activité, votre enfant préfère...",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Qu'on lui explique les règles et la progression en détail avant de commencer", scores: { PRE: 2 } },
      { id: "B", text: "Essayer directement sans trop d'explications — apprendre en faisant", scores: { INT: 2 } },
      { id: "C", text: "Observer les autres faire avant de se lancer lui-même", textF: "Observer les autres faire avant de se lancer elle-même", scores: { PRE: 1 } },
      { id: "D", text: "Inventer ses propres façons de faire — ne pas trop suivre les consignes", scores: { EXP: 2 } },
    ],
  },
  {
    id: "P7", text: "Si votre enfant a déjà abandonné une activité, pourquoi ?",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Trop répétitif — il s'ennuyait", textF: "Trop répétitif — elle s'ennuyait", scores: { EXP: 1, INT: 1 } },
      { id: "B", text: "Trop de compétition ou de pression — ça le stressait", textF: "Trop de compétition ou de pression — ça la stressait", scores: { COL: 1, EXP: 1 } },
      { id: "C", text: "Pas assez de résultats visibles rapidement", scores: { COM: 1, INT: 1 } },
      { id: "D", text: "Les autres enfants ou l'ambiance ne lui correspondaient pas", scores: { PRE: 1 } },
      { id: "E", text: "Il n'a jamais abandonné d'activité", textF: "Elle n'a jamais abandonné d'activité", scores: { INT: 1 } },
    ],
  },
  {
    id: "P8", text: "Comment décririez-vous le rapport de votre enfant à son corps ?",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Très à l'aise — sportif naturel, coordonné, aime l'effort et la dépense physique", textF: "Très à l'aise — sportive naturelle, coordonnée, aime l'effort et la dépense physique", scores: { INT: 2 } },
      { id: "B", text: "Précis et minutieux — préfère les gestes techniques et bien faits", textF: "Précise et minutieuse — préfère les gestes techniques et bien faits", scores: { PRE: 2 } },
      { id: "C", text: "Expressif — utilise son corps pour communiquer, ressentir et s'exprimer", textF: "Expressive — utilise son corps pour communiquer, ressentir et s'exprimer", scores: { EXP: 2 } },
      { id: "D", text: "Prudent — prend soin de lui, évite les risques physiques, préfère la sécurité", textF: "Prudente — prend soin d'elle, évite les risques physiques, préfère la sécurité", scores: { PRE: 1 } },
    ],
  },
  {
    id: "P9", text: "Qu'est-ce qui motive vraiment votre enfant dans une activité ?",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: "Gagner et être le meilleur — la compétition le stimule", textF: "Gagner et être la meilleure — la compétition la stimule", scores: { COM: 3 } },
      { id: "B", text: "Progresser et se dépasser lui-même — peu importe les autres", textF: "Progresser et se dépasser elle-même — peu importe les autres", scores: { PRE: 2 } },
      { id: "C", text: "S'exprimer et être regardé — il aime montrer ce qu'il sait faire", textF: "S'exprimer et être regardée — elle aime montrer ce qu'elle sait faire", scores: { SCE: 2, EXP: 1 } },
      { id: "D", text: "Appartenir à un groupe et partager — l'ambiance compte autant que l'activité", scores: { COL: 3 } },
      { id: "E", text: "Maîtriser parfaitement une technique — il veut faire les choses bien", textF: "Maîtriser parfaitement une technique — elle veut faire les choses bien", scores: { PRE: 2 } },
    ],
  },
  {
    id: "P10", text: "Votre enfant peut rester concentré sur une même chose pendant...",
    multi: false, maxSelect: 1,
    options: [
      { id: "A", text: "Moins de 15 minutes — il a besoin de changement et de variété", textF: "Moins de 15 minutes — elle a besoin de changement et de variété", scores: { INT: 2 } },
      { id: "B", text: "15 à 30 minutes si le sujet l'intéresse", scores: { COM: 1 } },
      { id: "C", text: "Plus de 30 minutes si le sujet le passionne vraiment", textF: "Plus de 30 minutes si le sujet la passionne vraiment", scores: { PRE: 1, SCE: 1 } },
      { id: "D", text: "Aussi longtemps que nécessaire — il peut être absorbé pendant des heures", textF: "Aussi longtemps que nécessaire — elle peut être absorbée pendant des heures", scores: { PRE: 2 } },
    ],
  },
  {
    id: "P11", text: "Imaginez votre enfant dans un spectacle ou une représentation devant un public. Il...",
    multi: false, maxSelect: 1,
    options: [
      { id: "A", text: "Adore ça — il est à l'aise devant le public, ça le stimule et l'énergise", textF: "Adore ça — elle est à l'aise devant le public, ça la stimule et l'énergise", scores: { SCE: 3 } },
      { id: "B", text: "Le fait volontiers mais sans en faire une priorité", scores: { SCE: 1 } },
      { id: "C", text: "Le fait mais est stressé — il préférerait éviter", textF: "Le fait mais est stressée — elle préférerait éviter", scores: { PRE: 1 } },
      { id: "D", text: "Refuse ou souffre vraiment — il n'aime pas être regardé", textF: "Refuse ou souffre vraiment — elle n'aime pas être regardée", scores: { PRE: 2 } },
    ],
  },
  {
    id: "P12", text: "Si vous deviez choisir la phrase qui décrit le mieux votre enfant...",
    multi: true, maxSelect: 2,
    options: [
      { id: "A", text: `"Il a une énergie débordante qu'il faut canaliser"`, textF: `"Elle a une énergie débordante qu'il faut canaliser"`, scores: { INT: 2 } },
      { id: "B", text: `"Il est dans sa bulle et crée son propre monde"`, textF: `"Elle est dans sa bulle et crée son propre monde"`, scores: { EXP: 1, PRE: 1 } },
      { id: "C", text: `"Il a besoin des autres pour vraiment s'épanouir"`, textF: `"Elle a besoin des autres pour vraiment s'épanouir"`, scores: { COL: 2 } },
      { id: "D", text: `"Il veut tout comprendre avant d'agir"`, textF: `"Elle veut tout comprendre avant d'agir"`, scores: { PRE: 1 } },
      { id: "E", text: `"Il veut être sur le devant de la scène"`, textF: `"Elle veut être sur le devant de la scène"`, scores: { SCE: 2 } },
      { id: "F", text: `"Il recherche la perfection dans tout ce qu'il fait"`, textF: `"Elle recherche la perfection dans tout ce qu'elle fait"`, scores: { PRE: 2 } },
    ],
  },
];

// ═══════════════════════════════════════════════════════
// CALCUL DU SCORE
// ═══════════════════════════════════════════════════════
function computeScores(childAnswers, parentAnswers) {
  const scores = { INT: 0, PRE: 0, EXP: 0, COM: 0, COL: 0, SCE: 0 };

  CHILD_QUESTIONS.forEach((q) => {
    const ans = childAnswers[q.id];
    if (!ans) return;
    const opt = q.options.find((o) => o.id === ans);
    if (opt) Object.entries(opt.scores).forEach(([k, v]) => { scores[k] += v; });
  });

  PARENT_QUESTIONS.forEach((q) => {
    const ans = parentAnswers[q.id];
    if (!ans || !ans.length) return;
    ans.forEach((sel) => {
      const opt = q.options.find((o) => o.id === sel);
      if (opt) Object.entries(opt.scores).forEach(([k, v]) => { scores[k] += v; });
    });
  });

  const total = Object.values(scores).reduce((a, b) => a + b, 0) || 1;
  const pct = {};
  Object.keys(scores).forEach((k) => { pct[k] = Math.round((scores[k] / total) * 100); });
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return { scores, pct, primary: sorted[0][0], secondary: sorted[1][0] };
}

// ═══════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════

function applyGenderQ(text, gender) {
  if (!gender) return text;
  const isF = gender === "Fille";
  const map = {
    "Après 3 mois d'entraînement, ce qui te rendrait le plus fier c'est...": { G: "Après 3 mois d'entraînement, ce qui te rendrait le plus fier c'est...", F: "Après 3 mois d'entraînement, ce qui te rendrait la plus fière c'est..." },
    "Pour montrer que tu es content, tu fais quoi en premier ?": { G: "Pour montrer que tu es content, tu fais quoi en premier ?", F: "Pour montrer que tu es contente, tu fais quoi en premier ?" },
    "Quand votre enfant a du temps libre non structuré, que fait-il spontanément ?": { G: "Quand votre fils a du temps libre non structuré, que fait-il spontanément ?", F: "Quand votre fille a du temps libre non structuré, que fait-elle spontanément ?" },
    "Quand votre enfant perd à un jeu ou rate quelque chose, il...": { G: "Quand votre fils perd à un jeu ou rate quelque chose, il...", F: "Quand votre fille perd à un jeu ou rate quelque chose, elle..." },
    "Dans une activité collective, votre enfant a naturellement tendance à...": { G: "Dans une activité collective, votre fils a naturellement tendance à...", F: "Dans une activité collective, votre fille a naturellement tendance à..." },
    "Qu'est-ce qui fatigue le plus votre enfant ?": { G: "Qu'est-ce qui fatigue le plus votre fils ?", F: "Qu'est-ce qui fatigue le plus votre fille ?" },
    "Dans quelle situation votre enfant vous semble le plus vivant, le plus lui-même ?": { G: "Dans quelle situation votre fils vous semble le plus vivant, le plus lui-même ?", F: "Dans quelle situation votre fille vous semble la plus vivante, la plus elle-même ?" },
    "Face à une nouvelle activité, votre enfant préfère...": { G: "Face à une nouvelle activité, votre fils préfère...", F: "Face à une nouvelle activité, votre fille préfère..." },
    "Si votre enfant a déjà abandonné une activité, pourquoi ?": { G: "Si votre fils a déjà abandonné une activité, pourquoi ?", F: "Si votre fille a déjà abandonné une activité, pourquoi ?" },
    "Comment décririez-vous le rapport de votre enfant à son corps ?": { G: "Comment décririez-vous le rapport de votre fils à son corps ?", F: "Comment décririez-vous le rapport de votre fille à son corps ?" },
    "Qu'est-ce qui motive vraiment votre enfant dans une activité ?": { G: "Qu'est-ce qui motive vraiment votre fils dans une activité ?", F: "Qu'est-ce qui motive vraiment votre fille dans une activité ?" },
    "Votre enfant peut rester concentré sur une même chose pendant...": { G: "Votre fils peut rester concentré sur une même chose pendant...", F: "Votre fille peut rester concentrée sur une même chose pendant..." },
    "Imaginez votre enfant dans un spectacle ou une représentation devant un public. Il...": { G: "Imaginez votre fils dans un spectacle ou une représentation devant un public. Il...", F: "Imaginez votre fille dans un spectacle ou une représentation devant un public. Elle..." },
    "Si vous deviez choisir la phrase qui décrit le mieux votre enfant...": { G: "Si vous deviez choisir la phrase qui décrit le mieux votre fils...", F: "Si vous deviez choisir la phrase qui décrit le mieux votre fille..." },
  };
  if (map[text]) return isF ? map[text].F : map[text].G;
  return text;
}

export default function MatriceB({ onBack }) {
  const [step, setStep] = useState(0); // 0=intro 1=child 2=parent 3=results
  const [childQ, setChildQ] = useState(0);
  const [parentQ, setParentQ] = useState(0);
  const [childAnswers, setChildAnswers] = useState({});
  const [parentAnswers, setParentAnswers] = useState({});
  const [childName, setChildName] = useState("");
  const [gender, setGender] = useState("");
  const [childAge, setChildAge] = useState("");
  const [result, setResult] = useState(null);

  const totalChildQ = CHILD_QUESTIONS.length;
  const totalParentQ = PARENT_QUESTIONS.length;

  function handleChildAnswer(qId, optId) {
    setChildAnswers((prev) => ({ ...prev, [qId]: optId }));
  }

  function handleParentAnswer(qId, optId, isMulti, maxSelect) {
    setParentAnswers((prev) => {
      const current = prev[qId] || [];
      if (!isMulti || maxSelect === 1) return { ...prev, [qId]: [optId] };
      if (current.includes(optId)) return { ...prev, [qId]: current.filter((x) => x !== optId) };
      if (current.length >= maxSelect) return { ...prev, [qId]: [...current.slice(1), optId] };
      return { ...prev, [qId]: [...current, optId] };
    });
  }

  function goNextChild() {
    if (childQ < totalChildQ - 1) setChildQ(childQ + 1);
    else setStep(2);
  }
  function goPrevChild() {
    if (childQ > 0) setChildQ(childQ - 1);
    else setStep(0);
  }
  function goNextParent() {
    if (parentQ < totalParentQ - 1) setParentQ(parentQ + 1);
    else {
      const r = computeScores(childAnswers, parentAnswers);
      setResult(r);
      setStep(3);
    }
  }
  function goPrevParent() {
    if (parentQ > 0) setParentQ(parentQ - 1);
    else setStep(1);
  }

  const progressChild = Math.round(((childQ + 1) / totalChildQ) * 100);
  const progressParent = Math.round(((parentQ + 1) / totalParentQ) * 100);

  // ── STYLES ──
  const s = {
    wrap: { fontFamily: "'Fraunces', Georgia, serif", background: "#F5F2FF", minHeight: "100vh", padding: "0" },
    card: { background: "white", borderRadius: 20, padding: "32px 28px", maxWidth: 640, margin: "0 auto", boxShadow: "0 4px 32px rgba(61,43,142,0.08)" },
    h1: { fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 700, color: "#3D2B8E", margin: "0 0 8px" },
    h2: { fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, color: "#3D2B8E", margin: "0 0 16px", lineHeight: 1.3 },
    body: { fontFamily: "system-ui, sans-serif", fontSize: 15, color: "#4A3F72", lineHeight: 1.6 },
    small: { fontFamily: "system-ui, sans-serif", fontSize: 12, color: "#8B7BB5" },
    btn: { background: "#3D2B8E", color: "white", border: "none", borderRadius: 100, padding: "12px 28px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui, sans-serif" },
    btnOutline: { background: "transparent", color: "#3D2B8E", border: "2px solid #C8B6F5", borderRadius: 100, padding: "10px 24px", fontSize: 14, cursor: "pointer", fontFamily: "system-ui, sans-serif" },
    label: { fontFamily: "system-ui, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#9B7FE8", textTransform: "uppercase", marginBottom: 4 },
    progress: { height: 4, background: "#EDE8FF", borderRadius: 4, overflow: "hidden", margin: "0 0 24px" },
    progressFill: (pct) => ({ height: "100%", background: "#6B4FBB", borderRadius: 4, width: pct + "%", transition: "width 0.4s" }),
    header: { background: "linear-gradient(135deg, #3D2B8E, #6B4FBB)", padding: "20px 28px 16px", color: "white" },
  };

  // ── ÉCRAN INTRO ──
  if (step === 0) return (
    <div style={s.wrap}>
      <div style={{ ...s.header, borderRadius: "0 0 24px 24px" }}>
        <div style={s.label}>Questionnaire B · 8-11 ans</div>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "white", margin: "4px 0 4px" }}>Quel est le profil de votre enfant ?</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }}>Trouvons les activités qui lui correspondent vraiment</div>
      </div>
      <div style={{ padding: 20 }}>
        <div style={s.card}>
          <div style={{ ...s.label, marginBottom: 16 }}>Avant de commencer</div>
          <div style={{ ...s.body, marginBottom: 20 }}>
            Ce questionnaire se remplit en <b>deux temps</b> :<br /><br />
            <span style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ background: "#EDE8FF", color: "#3D2B8E", borderRadius: 100, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>1</span>
              <span><b>Votre enfant répond seul</b> à 8 questions illustrées (10 min)</span>
            </span>
            <span style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ background: "#EDE8FF", color: "#3D2B8E", borderRadius: 100, width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>2</span>
              <span><b>Vous répondez seul</b> à 12 questions d'observation (10 min)</span>
            </span>
          </div>
          <div style={{ background: "#FDF6E3", borderLeft: "4px solid #E8C87A", padding: "12px 16px", borderRadius: "0 8px 8px 0", ...s.body, marginBottom: 24 }}>
            Ne montrez pas vos réponses à votre enfant — et réciproquement. Le croisement de deux regards indépendants produit les meilleures recommandations.
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ ...s.small, marginBottom: 6 }}>Prénom de l'enfant</div>
            <input value={childName} onChange={e => setChildName(e.target.value)}
              placeholder="Prénom..." style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #C8B6F5", borderRadius: 10, fontSize: 15, fontFamily: "system-ui, sans-serif", boxSizing: "border-box", color: "#1A1035" }} />
          </div>
          <div style={{ marginBottom: 28 }}>
            <div style={{ ...s.small, marginBottom: 6 }}>Âge</div>
            <input value={childAge} onChange={e => setChildAge(e.target.value)}
              placeholder="Ex: 9 ans"
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, color: "#8B7BB5", marginBottom: 8 }}>Votre enfant est...</div>
            <div style={{ display: "flex", gap: 12 }}>
              {["Fille", "Garçon"].map(g => (
                <div key={g} onClick={() => setGender(g)}
                  style={{ flex: 1, padding: "10px", borderRadius: 12, cursor: "pointer", textAlign: "center", border: gender === g ? "2px solid #6B4FBB" : "2px solid #EDE8FF", background: gender === g ? "#EDE8FF" : "white", fontWeight: gender === g ? 700 : 400, color: gender === g ? "#3D2B8E" : "#1A1035", fontSize: 14 }}>
                  {g}
                </div>
              ))}
            </div>
          </div>
          <button style={s.btn} onClick={() => gender && setStep(1)}>Commencer le questionnaire →</button>
        </div>
      </div>
    </div>
  );

  // ── QUESTIONS ENFANT ──
  if (step === 1) {
    const q = CHILD_QUESTIONS[childQ];
    const ans = childAnswers[q.id];
    return (
      <div style={s.wrap}>
        <div style={{ ...s.header, borderRadius: "0 0 24px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={s.label}>Questions pour {childName || "l'enfant"}</div>
            <div style={{ ...s.small, color: "rgba(255,255,255,0.6)" }}>{childQ + 1} / {totalChildQ}</div>
          </div>
          <div style={s.progressFill(progressChild)} />
          <div style={{ ...s.progress, background: "rgba(255,255,255,0.2)", marginBottom: 0 }}>
            <div style={{ height: "100%", background: "#E8C87A", borderRadius: 4, width: progressChild + "%", transition: "width 0.4s" }} />
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <div style={s.card}>
            <div style={{ ...s.small, marginBottom: 10, color: "#9B7FE8" }}>Une seule réponse</div>
            <h2 style={s.h2}>{applyGenderQ(q.text, gender)}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
              {q.options.map((opt) => {
                const selected = ans === opt.id;
                return (
                  <div key={opt.id} onClick={() => handleChildAnswer(q.id, opt.id)}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 14, cursor: "pointer", border: selected ? "2px solid #6B4FBB" : "2px solid #EDE8FF", background: selected ? "#EDE8FF" : "white", transition: "all 0.15s" }}>
                    <span style={{ fontSize: 26, flexShrink: 0 }}>{opt.emoji}</span>
                    <span style={{ ...s.body, fontWeight: selected ? 600 : 400, color: selected ? "#3D2B8E" : "#1A1035" }}>{gender === "Fille" ? (opt.textF || opt.text) : opt.text}</span>
                    {selected && <span style={{ marginLeft: "auto", color: "#6B4FBB", fontSize: 18 }}>✓</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={s.btnOutline} onClick={goPrevChild}>← Retour</button>
              <button style={{ ...s.btn, opacity: !ans ? 0.4 : 1 }} onClick={goNextChild} disabled={!ans}>
                {childQ < totalChildQ - 1 ? "Suivant →" : "Terminer ma partie →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── QUESTIONS PARENT ──
  if (step === 2) {
    const q = PARENT_QUESTIONS[parentQ];
    const ans = parentAnswers[q.id] || [];
    const canNext = q.multi ? ans.length > 0 : ans.length === 1;
    return (
      <div style={s.wrap}>
        <div style={{ ...s.header, borderRadius: "0 0 24px 24px", background: "linear-gradient(135deg, #1B5E7D, #2E7D6B)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ ...s.label, color: "rgba(255,255,255,0.7)" }}>Questions pour les parents</div>
            <div style={{ ...s.small, color: "rgba(255,255,255,0.6)" }}>{parentQ + 1} / {totalParentQ}</div>
          </div>
          <div style={{ ...s.progress, background: "rgba(255,255,255,0.2)", marginBottom: 0 }}>
            <div style={{ height: "100%", background: "#E8C87A", borderRadius: 4, width: progressParent + "%", transition: "width 0.4s" }} />
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <div style={s.card}>
            <div style={{ ...s.small, marginBottom: 10, color: "#1B6B6B" }}>
              {q.multi ? `Cochez ${q.maxSelect} réponse${q.maxSelect > 1 ? "s" : ""}` : "Une seule réponse"}
            </div>
            <h2 style={{ ...s.h2, color: "#1B5E7D" }}>{applyGenderQ(q.text, gender)}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
              {q.options.map((opt) => {
                const selected = ans.includes(opt.id);
                return (
                  <div key={opt.id} onClick={() => handleParentAnswer(q.id, opt.id, q.multi, q.maxSelect)}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderRadius: 12, cursor: "pointer", border: selected ? "2px solid #1B6B6B" : "2px solid #E0F4F4", background: selected ? "#E0F4F4" : "white", transition: "all 0.15s" }}>
                    <span style={{ width: 20, height: 20, borderRadius: q.multi ? 4 : 100, border: selected ? "none" : "2px solid #A0C4C4", background: selected ? "#1B6B6B" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: "white", transition: "all 0.15s" }}>
                      {selected ? "✓" : ""}
                    </span>
                    <span style={{ ...s.body, fontSize: 14, fontWeight: selected ? 600 : 400, color: selected ? "#1B5E7D" : "#1A1035" }}>{gender === "Fille" ? (opt.textF || opt.text) : opt.text}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button style={s.btnOutline} onClick={goPrevParent}>← Retour</button>
              <button style={{ ...s.btn, background: "#1B6B6B", opacity: !canNext ? 0.4 : 1 }} onClick={goNextParent} disabled={!canNext}>
                {parentQ < totalParentQ - 1 ? "Suivant →" : "Voir le profil →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── RÉSULTATS ──
  if (step === 3 && result) {
    const P = PROFILES[result.primary];
    const S = PROFILES[result.secondary];
    const TC = "#1B6B6B";
    return (
      <div style={{ fontFamily: "system-ui", background: "#F5FAFA", minHeight: "100vh" }}>
        <div style={{ background: `linear-gradient(135deg, ${P?.color || TC}, ${S?.color || TC})`, padding: "40px 20px 32px", borderRadius: "0 0 32px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>{P?.emoji}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{P?.name}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", marginBottom: 16 }}>Profil principal</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.15)", borderRadius: 100, padding: "6px 16px" }}>
            <span>{S?.emoji}</span>
            <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{S?.name} · profil secondaire</span>
          </div>
        </div>
        <div style={{ padding: "32px 20px", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", marginBottom: 20, boxShadow: "0 4px 24px rgba(27,107,107,0.08)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0D2B2B", margin: "0 0 12px" }}>
              Votre rapport personnalisé est prêt
            </h2>
            <p style={{ fontSize: 14, color: "#4A3F72", lineHeight: 1.7, margin: "0 0 24px" }}>
              Le rapport PDF contient le profil complet, les 4 dimensions, les activités recommandées et les conseils aux parents.
            </p>
            <GenerateReport childName={childName} childAge={childAge} gender={gender} primaryKey={result.primary} secondaryKey={result.secondary} scores={result.pct} questionnaire="B" />
          </div>
          <button style={{ background: "transparent", color: TC, border: "2px solid #1B6B6B", borderRadius: 100, padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui", marginTop: 8 }} onClick={onBack || (() => window.location.href = "/")}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return null;
}